// When moving between Rana frontend and the HCC frontends (hcc-live-site and hcc-management-screens)
// the browser should remember what the Rana tenant was and what its organisation was.
// Rana tenants can have 0 or more organisations; we use the first, if present.

// This is the initial "light" implementation. All we support are links to the homepages of the apps
// and the existing links to simulations / models from HCC Management to Live Site. A ADR will
// be written for a more feature rich version.

// All existing functionality should still work for people used to the old 3Di frontends; so things
// like switching organisations (that are not connected to the same tenant) will still be possible.

// Also we can't use more complicated URLs (without a little extra work) because the authentication
// redirect dance that HCC frontends do redirect to the front page at the end and extra information
// in the URL is lost if the user was not logged in to the HCC API yet.

// Solution:
// - Rana sets localstorage variables for its latest tenant and the connected 3Di organisation
// - The frontends use that tenant for links back to Rana (and link to Rana's front page if there is no such tenant)
// - The frontends use that organisation to select the initially selected organisation.

const LOCALSTORAGE_KEY = "rana_localstorage_key_tenant_and_organisation";

export function storeTenantAndOrganisation(tenant: string, tenantOrganisations: string[]): void {
  // Tenant data refreshes after authentication; preserve a deliberate selection
  // instead of replacing it with the tenant's first organisation every time.
  const stored = retrieveTenantAndOrganisation();
  const storedOrganisationIsLinked =
    stored.tenant === tenant &&
    stored.tenantOrganisation !== null &&
    // Organisation IDs may differ only in UUID casing or hyphen formatting.
    tenantOrganisations.some((organisation) =>
      compareOrganisationIds(organisation, stored.tenantOrganisation as string)
    );
  const tenantOrganisation = storedOrganisationIsLinked
    ? stored.tenantOrganisation
    : tenantOrganisations[0] ?? null;

  if (window) {
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify({ tenant, tenantOrganisation }));
  }
}

export function storeSelectedOrganisation(tenant: string, organisationId: string): void {
  if (window) {
    window.localStorage.setItem(
      LOCALSTORAGE_KEY,
      JSON.stringify({ tenant, tenantOrganisation: organisationId })
    );
  }
}

export function retrieveTenantAndOrganisation(): {
  tenant: string | null;
  tenantOrganisation: string | null;
} {
  if (window) {
    const value = window.localStorage.getItem(LOCALSTORAGE_KEY);
    if (value) {
      try {
        const parsed = JSON.parse(value);
        if (
          parsed &&
          typeof parsed.tenant === "string" &&
          (typeof parsed.tenantOrganisation === "string" || parsed.tenantOrganisation === null)
        ) {
          return {
            tenant: parsed.tenant,
            tenantOrganisation: parsed.tenantOrganisation,
          };
        }
      } catch (error) {
        // Parse error, ignore.
      }
    }
  }

  return {
    tenant: null,
    tenantOrganisation: null,
  };
}

export function hasRanaTenant(): boolean {
  return retrieveTenantAndOrganisation().tenant !== null;
}

export function hasRanaOrganisation(): boolean {
  return retrieveTenantAndOrganisation().tenantOrganisation !== null;
}

export function compareOrganisationIds(a: string, b: string): boolean {
  // Sometimes UUIDs have '-' characters in them, sometimes they don't 🙄
  return a.toLowerCase().replace(/-/g, "") === b.toLowerCase().replace(/-/g, "");
}
